<template>
    <div class="site-width uk-animation-fade single">
        <Seo canonical="/charity-run-checkin" title="Check-in" />
        <div class="menu-headline-distance" />

        <!-- Password gate -->
        <div
            v-if="!authenticated"
            class="password-gate uk-card uk-card-alternative uk-padding uk-text-center">
            <h2 class="uk-margin-bottom" style="color: #ddd">
                Check-in Passwort
            </h2>
            <div v-if="scanError" class="scan-error uk-margin-small-bottom">
                {{ scanError }}
            </div>
            <input
                v-model="password"
                class="uk-input uk-margin-bottom"
                type="password"
                placeholder="Passwort eingeben"
                @keyup.enter="authenticate" />
            <Button
                has-action
                :action="authenticate"
                mode="primary"
                is-large
                class="uk-width-1-1">
                Weiter
            </Button>
        </div>

        <template v-else>
            <SiteHeadline
                is-main
                :headline="'Check-in' + (eventName ? ' – ' + eventName : '')"
                :message="
                    hasCamera
                        ? 'Scanne den QR-Code des Läufers.'
                        : 'Gib ID, E-Mail oder Name ein.'
                " />
            <div class="headline-message-distance" />

            <!-- Live counts, refreshed after every check-in -->
            <div v-if="stats" class="stats">
                <div class="stats-bar">
                    <div class="stats-fill" :style="{width: percent + '%'}" />
                </div>
                <div class="stats-main">
                    <strong>{{ stats.present }}</strong> von
                    {{ stats.total }} eingecheckt
                    <span class="stats-pct">{{ percent }}%</span>
                </div>
                <div class="stats-row">
                    <span
                        v-for="d in stats.distances"
                        :key="d.distance_km"
                        class="stats-chip">
                        {{ d.distance_km }} km
                        <strong>{{ d.present }}</strong>/{{ d.total }}
                    </span>
                </div>
            </div>

            <!-- Scanner + input -->
            <div v-if="!result && !candidates.length" class="scanner-wrap">
                <!-- QR reader: only shown when camera available -->
                <div v-if="hasCamera" id="qr-reader" class="qr-reader" />

                <div v-if="scanError" class="scan-error">{{ scanError }}</div>

                <!-- OR divider: only when camera is present -->
                <div v-if="hasCamera" class="divider-text">oder</div>

                <!-- Round pill input -->
                <div class="pill-row">
                    <input
                        v-model="manualInput"
                        class="pill-input"
                        type="text"
                        placeholder="Startcode, Name oder E-Mail"
                        @keyup.enter="lookup" />
                    <button
                        class="pill-btn"
                        :disabled="!manualInput || loading"
                        @click="lookup">
                        <span v-if="loading">⏳</span>
                        <span v-else>→</span>
                    </button>
                </div>
            </div>

            <!-- Candidates: nobody is checked in until one is picked -->
            <div v-else-if="!result" class="scanner-wrap">
                <div class="candidate-hint">
                    {{ candidates.length }} Treffer – bitte auswählen:
                </div>
                <div v-if="scanError" class="scan-error">{{ scanError }}</div>

                <button
                    v-for="candidate in candidates"
                    :key="candidate.id"
                    class="candidate"
                    :class="{'is-done': candidate.participated}"
                    @click="select(candidate)">
                    <span class="candidate-name">
                        {{ candidate.full_name }}
                        <span v-if="candidate.checkin_code" class="candidate-code">{{
                            candidate.checkin_code
                        }}</span>
                    </span>
                    <span class="candidate-meta">
                        {{ candidate.distance_km }} km<template
                            v-if="candidate.team_name"
                        >
                            · {{ candidate.team_name }}</template
                        ><template v-if="candidate.participated">
                            · bereits eingecheckt</template
                        >
                    </span>
                </button>

                <div class="uk-text-center">
                    <button class="candidate-cancel" @click="cancelSelection">
                        Abbrechen
                    </button>
                </div>
            </div>

            <!-- Result -->
            <div
                v-else
                class="result-card uk-card uk-card-alternative uk-padding uk-text-center">
                <div
                    class="result-status"
                    :class="result.already_checked_in ? 'warning' : 'success'">
                    {{
                        result.already_checked_in
                            ? '⚠️ Bereits eingecheckt'
                            : '✅ Eingecheckt!'
                    }}
                </div>
                <div class="result-name">{{ result.full_name }}</div>
                <div class="result-meta">
                    <span class="result-badge"
                        >{{ result.distance_km }} km</span
                    >
                    <span v-if="result.team_name" class="result-team">{{
                        result.team_name
                    }}</span>
                </div>
                <div class="result-event">{{ result.event }}</div>
                <div class="uk-margin-medium-top">
                    <Button has-action :action="reset" mode="success" is-large>
                        Nächste Person →
                    </Button>
                </div>
            </div>
        </template>

        <div class="uk-margin-large-top" />
    </div>
</template>

<script>
import Seo from '../../components/Seo.vue';
import SiteHeadline from '@/components/SiteHeadline.vue';
import Button from '../../components/Button.vue';
import axios from 'axios';
import {Html5Qrcode} from 'html5-qrcode';

export default {
    name: 'CharityRunCheckin',
    components: {Seo, SiteHeadline, Button},
    data() {
        return {
            scanner: null,
            result: null,
            candidates: [],
            scanError: null,
            manualInput: '',
            loading: false,
            password: '',
            authenticated: false,
            eventName: null,
            hasCamera: false, // determined after auth
            stats: null,
            // Runner id from a scanned QR mail, consumed once we are past the
            // password gate.
            pendingId: null,
        };
    },
    computed: {
        eventSlug() {
            return this.$route.query.event || null;
        },
        percent() {
            if (!this.stats || !this.stats.total) return 0;
            return Math.round((this.stats.present / this.stats.total) * 100);
        },
    },
    async mounted() {
        // Both one-shot params are read and then scrubbed from the URL in a
        // single replace: a shareable link carries the password, which has no
        // business staying in the address bar, in history, or in a screenshot.
        this.pendingId = this.$route.query.id || null;
        const linkPassword = this.$route.query.pw || null;

        if (this.pendingId || linkPassword) {
            const query = {...this.$route.query};
            delete query.id;
            delete query.pw;
            this.$router.replace({query}).catch(() => {});
        }

        const password = linkPassword || sessionStorage.getItem('checkin_pw');
        if (!password) return;

        this.password = password;

        // A stored password can outlive a password change, so check it now
        // rather than at the first scan.
        if (await this.passwordAccepted()) {
            sessionStorage.setItem('checkin_pw', password);
            this.authenticated = true;
            this.$nextTick(() => this.afterAuth());
            return;
        }

        sessionStorage.removeItem('checkin_pw');
        this.password = '';
    },
    beforeDestroy() {
        this.stopScanner();
    },
    methods: {
        async authenticate() {
            if (!this.eventSlug) {
                this.scanError =
                    'Kein Event angegeben. Bitte URL mit ?event=slug aufrufen.';
                return;
            }
            if (!this.password || this.loading) return;

            this.loading = true;
            this.scanError = null;

            const accepted = await this.passwordAccepted();
            this.loading = false;

            if (accepted) {
                sessionStorage.setItem('checkin_pw', this.password);
                this.authenticated = true;
                this.$nextTick(() => this.afterAuth());
            }
        },

        // The gate used to let any text through and only fail at the first
        // scan, where a 401 surfaced as "Person nicht gefunden" – so a typo in
        // the password looked like a broken scanner.
        async passwordAccepted() {
            try {
                await axios.get(
                    this.getBaseUrl + '/api/charity-runs/checkin/verify',
                    {headers: {'checkin-password': this.password}}
                );
                return true;
            } catch (err) {
                this.scanError =
                    err.response && err.response.status === 401
                        ? 'Passwort falsch.'
                        : 'Server nicht erreichbar. Bitte erneut versuchen.';
                return false;
            }
        },

        // A 401 mid-session must send the volunteer back to the gate instead
        // of reading as a missing runner.
        unauthorize() {
            this.stopScanner();
            sessionStorage.removeItem('checkin_pw');
            this.authenticated = false;
            this.password = '';
            this.result = null;
            this.candidates = [];
            this.scanError = 'Passwort falsch. Bitte erneut eingeben.';
        },

        // A ?id= from a scanned QR mail wins over opening the camera. It is
        // cleared as it is used, so a reload or "Nächste Person" cannot check
        // the same runner in twice.
        afterAuth() {
            this.loadStats();
            if (this.pendingId) {
                const id = this.pendingId;
                this.pendingId = null;
                this.checkinById(id);
                return;
            }
            this.initCamera();
        },

        // Probe for a camera first; only start scanner if one exists.
        async initCamera() {
            try {
                const devices = await Html5Qrcode.getCameras();
                if (devices && devices.length > 0) {
                    this.hasCamera = true;
                    this.$nextTick(() => this.startScanner());
                }
                // No cameras → hasCamera stays false, no scanner, no error shown
            } catch {
                // Permission denied or no camera API → silently stay in manual mode
            }
        },

        startScanner() {
            this.$nextTick(() => {
                this.scanner = new Html5Qrcode('qr-reader');
                this.scanner
                    .start(
                        {facingMode: 'environment'},
                        {fps: 10, qrbox: {width: 250, height: 250}},
                        (decodedText) => {
                            this.stopScanner();
                            this.checkinById(this.extractId(decodedText));
                        },
                        () => {}
                    )
                    .catch(() => {
                        // Camera disappeared after probe – fall back silently
                        this.hasCamera = false;
                        this.scanner = null;
                    });
            });
        },

        stopScanner() {
            if (this.scanner) {
                // Only stop if the scanner is actually running
                try {
                    const s = this.scanner;
                    this.scanner = null;
                    if (s.isScanning) {
                        s.stop().catch(() => {});
                    }
                } catch {
                    // ignore
                }
            }
        },

        extractId(text) {
            // /checkin/123  (old format)
            const pathMatch = text.match(/\/checkin\/(\d+)/);
            if (pathMatch) return pathMatch[1];
            // ?id=123  (new format)
            try {
                const url = new URL(text);
                const id = url.searchParams.get('id');
                if (id) return id;
            } catch { /* empty */ }
            return text;
        },

        checkinById(id) {
            this.loading = true;
            axios
                .post(
                    this.getBaseUrl + '/api/charity-runs/checkin/' + id,
                    {event_slug: this.eventSlug},
                    {headers: {'checkin-password': this.password}}
                )
                .then((res) => {
                    this.result = res.data;
                    this.eventName = res.data.event;
                    this.loadStats();
                })
                .catch((err) => {
                    if (err.response && err.response.status === 401) {
                        this.unauthorize();
                        return;
                    }
                    this.scanError = 'Person nicht gefunden.';
                    // Arriving via a QR link means the camera was never probed.
                    if (this.hasCamera) this.startScanner();
                    else this.initCamera();
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        // Counts are a nice-to-have, so a failure here must never surface as
        // an error over the actual check-in.
        loadStats() {
            axios
                .get(this.getBaseUrl + '/api/charity-runs/checkin/stats', {
                    params: {event_slug: this.eventSlug},
                    headers: {'checkin-password': this.password},
                })
                .then((res) => {
                    this.stats = res.data;
                })
                .catch(() => {});
        },

        // Only ever reads. Nobody is checked in until a name is tapped – a
        // partial name matches several runners, and there is no undo.
        lookup() {
            if (!this.manualInput) return;
            this.stopScanner();
            this.loading = true;
            this.scanError = null;
            axios
                .post(
                    this.getBaseUrl + '/api/charity-runs/checkin/lookup',
                    {query: this.manualInput, event_slug: this.eventSlug},
                    {headers: {'checkin-password': this.password}}
                )
                .then((res) => {
                    this.candidates = res.data.registrations;
                    this.eventName = res.data.event;
                    if (res.data.truncated) {
                        this.scanError =
                            'Nur die ersten ' +
                            res.data.registrations.length +
                            ' von ' +
                            res.data.matches +
                            ' Treffern – bitte genauer eingeben.';
                    }
                })
                .catch((err) => {
                    if (err.response && err.response.status === 401) {
                        this.unauthorize();
                        return;
                    }
                    this.scanError = 'Person nicht gefunden.';
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        select(candidate) {
            this.candidates = [];
            this.manualInput = '';
            this.checkinById(candidate.id);
        },

        cancelSelection() {
            this.candidates = [];
            this.scanError = null;
            this.$nextTick(() => {
                if (this.hasCamera) this.startScanner();
                else this.initCamera();
            });
        },

        reset() {
            this.result = null;
            this.candidates = [];
            this.scanError = null;
            this.manualInput = '';
            this.$nextTick(() => {
                if (this.hasCamera) this.startScanner();
                else this.initCamera();
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.password-gate {
    max-width: 360px;
    margin: 0 auto;
}

.scanner-wrap {
    max-width: 400px;
    margin: 0 auto;
}

.qr-reader {
    border-radius: 12px;
    overflow: hidden;
    background: #000;
}

.scan-error {
    color: #f44336;
    font-size: 14px;
    text-align: center;
    margin-top: 12px;
}

.divider-text {
    text-align: center;
    font-size: 13px;
    opacity: 0.4;
    margin: 20px 0 16px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

/* Pill-style input row */
.pill-row {
    display: flex;
    align-items: center;
    background: #1e1e26;
    border: 1px solid #333;
    border-radius: 999px;
    padding: 6px 6px 6px 20px;
    gap: 8px;
}

.pill-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #eee;
    font-size: 15px;

    &::placeholder {
        color: #666;
    }
}

.pill-btn {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: #3ddc97;
    color: #121217;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;

    &:disabled {
        opacity: 0.35;
        cursor: default;
    }

    &:not(:disabled):hover {
        opacity: 0.85;
    }
}

/* Live counts */
.stats {
    max-width: 400px;
    margin: 0 auto 24px;
}

.stats-bar {
    height: 6px;
    border-radius: 999px;
    background: #1e1e26;
    overflow: hidden;
}

.stats-fill {
    height: 100%;
    background: #3ddc97;
    border-radius: 999px;
    transition: width 0.3s ease;
}

.stats-main {
    font-size: 14px;
    opacity: 0.75;
    margin-top: 8px;

    strong {
        font-size: 17px;
        color: #3ddc97;
    }
}

.stats-pct {
    float: right;
    opacity: 0.5;
}

.stats-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}

.stats-chip {
    background: #1e1e26;
    border: 1px solid #333;
    border-radius: 999px;
    padding: 3px 12px;
    font-size: 13px;
    opacity: 0.8;

    strong {
        color: #3ddc97;
    }
}

/* Candidate list */
.candidate-hint {
    font-size: 13px;
    opacity: 0.5;
    margin-bottom: 12px;
    text-align: center;
}

.candidate {
    display: block;
    width: 100%;
    text-align: left;
    background: #1e1e26;
    border: 1px solid #333;
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 10px;
    color: #eee;
    cursor: pointer;
    transition: border-color 0.15s;

    &:hover {
        border-color: #3ddc97;
    }

    &.is-done {
        opacity: 0.45;
    }
}

.candidate-name {
    display: block;
    font-size: 17px;
    font-weight: 700;
}

.candidate-code {
    float: right;
    font-family: monospace;
    font-size: 15px;
    letter-spacing: 0.08em;
    color: #3ddc97;
}

.candidate-meta {
    display: block;
    font-size: 13px;
    opacity: 0.6;
    margin-top: 2px;
}

.candidate-cancel {
    background: none;
    border: none;
    color: #eee;
    opacity: 0.6;
    font-size: 14px;
    padding: 12px 20px;
    margin-top: 8px;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        opacity: 1;
    }
}

/* Result card */
.result-card {
    max-width: 400px;
    margin: 0 auto;
    border-radius: 16px;
}

.result-status {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 16px;

    &.success {
        color: #3ddc97;
    }
    &.warning {
        color: #f0a030;
    }
}

.result-name {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 12px;
}

.result-meta {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-bottom: 8px;
}

.result-badge {
    background: #3ddc97;
    color: #121217;
    border-radius: 20px;
    padding: 4px 14px;
    font-weight: 700;
    font-size: 15px;
}

.result-team {
    background: #1e1e26;
    border: 1px solid #333;
    border-radius: 20px;
    padding: 4px 14px;
    font-size: 15px;
}

.result-event {
    font-size: 14px;
    opacity: 0.5;
    margin-top: 4px;
}
</style>

